import React, { useEffect, useId, useRef, useState } from 'react';
import { Alert, Button, CloseButton, Modal, Spinner } from 'react-bootstrap';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import PreviewLockNote from '@/components/common/PreviewLockNote';

const FALLBACK_ERROR = 'This could not be completed. Try again.';

/**
 * Asks the student to confirm a withdrawal before anything is sent.
 * `onConfirm` returns a promise; the parent closes the dialog when it
 * resolves, and a rejection's server message is shown here with the dialog
 * left open. One request goes out per confirmation.
 */
function WithdrawConfirmModal({
    show,
    title,
    body,
    confirmLabel = 'Withdraw',
    confirmDisabled = false,
    onConfirm,
    onCancel,
}) {
    const titleId = useId();
    const descriptionId = useId();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);
    const previewLocked = usePreviewLock();
    const errorRef = useRevealAlert(error);

    useEffect(() => {
        if (show) setError(null);
    }, [show]);

    const close = () => {
        if (!inFlight.current) onCancel();
    };

    const confirm = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setSubmitting(true);
        setError(null);
        try {
            await onConfirm();
        } catch (err) {
            setError(err?.response?.data?.message || FALLBACK_ERROR);
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={close}
            animation={false}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
        >
            <Modal.Header>
                <Modal.Title id={titleId} as="h2" className="h5">
                    {title}
                </Modal.Title>
                <CloseButton
                    aria-label="Close"
                    onClick={close}
                    disabled={submitting}
                />
            </Modal.Header>
            <Modal.Body>
                <div id={descriptionId}>{body}</div>
                {error && (
                    <Alert
                        variant="danger"
                        className="mt-3 mb-0"
                        ref={errorRef}
                        tabIndex={-1}
                    >
                        {error}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <PreviewLockNote locked={previewLocked} />
                <Button
                    variant="outline-primary"
                    onClick={close}
                    disabled={submitting}
                    autoFocus
                >
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    onClick={confirm}
                    disabled={submitting || previewLocked || confirmDisabled}
                    aria-describedby={
                        previewLocked ? PREVIEW_LOCK_NOTE_ID : undefined
                    }
                >
                    {submitting && (
                        <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                            aria-hidden="true"
                        />
                    )}
                    {confirmLabel}
                    {submitting && (
                        <span className="visually-hidden"> in progress</span>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default WithdrawConfirmModal;
